import React, { useCallback } from 'react';
import { Card, Badge, Text, Heading, Loading } from '../core';
import { Table } from '../core/Table';
import {
  IChain,
  useCall2,
  secDuration,
  useStrategySubgraph,
  formatPercentageBN,
  formatDollarsBN,
  formatAmountBN,
  useCorrectNetwork,
} from '@sc1/common';
import { useHlpStrategy, useSearchParam, useSend } from '@sc1/common/hooks';
import { ShortenedAddress } from '../ShortenedAddress';
import type { CardProps } from 'theme-ui';
import { StatusButton } from '../StatusButton';

interface PublicStrategyInfoProps extends CardProps {
  address: string;
}

export const PublicStrategyInfo = ({
  address,
  ...rest
}: PublicStrategyInfoProps): JSX.Element => {
  const chain = useSearchParam().get('chain', 'all chains') as IChain;
  const isCorrectNetwork = useCorrectNetwork(chain);
  const { loading, data } = useStrategySubgraph(chain, address);
  const { id, owner, name, decimals, tvl } = data?.strategy || {};

  const { address: timelockAddress, minDelay } = data?.timelocks[0] || {};

  const strategy = useHlpStrategy(address);

  // calculate rebalance offset
  const { res: tvlArray } = useCall2(strategy?.getTVL);
  const [
    tvl2,
    collateralBalance,
    shortPosition,
    borrowBalance,
    lpBalance,
    underlyingBalance,
  ] = tvlArray || [];
  const currentLp = lpBalance?.div(2);
  const rebalanceOffsetBN =
    borrowBalance &&
    currentLp &&
    borrowBalance &&
    (borrowBalance?.eq(0)
      ? 10000
      : currentLp?.sub(borrowBalance).mul(10000).div(borrowBalance));

  // calculate loan health
  const { res: minLoanHealthBN } = useCall2(strategy?.minLoanHealth);
  const { res: loanHealthBN } = useCall2(strategy?.loanHealth);
  const { res: rebalanceThresholdBN } = useCall2(strategy?.rebalanceThreshold);
  const badLoanHealth =
    !loanHealthBN?.eq('10000') && // contract bug
    loanHealthBN?.gt(0) &&
    minLoanHealthBN?.gt(0) &&
    loanHealthBN?.lt(minLoanHealthBN);

  const { sendTx, txStatus } = useSend();

  const handleSubmit = useCallback(() => {
    strategy && sendTx(strategy, 'rebalanceLoan', []);
  }, [strategy, sendTx]);

  return (
    <Card {...rest}>
      <Loading loading={loading}>
        <Heading as="h3" mb={2}>
          {name}
          <Text sx={{ fontSize: 2 }} mr={2} />
        </Heading>
        <Table
          columns={[1, 2]}
          index={false}
          data={[
            [
              'Strategy Contract',
              <ShortenedAddress key="contract" address={id} />,
            ],
            [
              'Strategy Owner',
              <ShortenedAddress key="owner" address={owner} />,
            ],
            [
              'Timelock Contract',
              <ShortenedAddress key={id} address={timelockAddress} />,
            ],
            ['Timelock Delay', secDuration(Number(minDelay))],
            ['TVL', formatDollarsBN(tvl, decimals, false)],
          ]}
        />
        {isCorrectNetwork && (
          <>
            <Table
              columns={[1, 2]}
              index={false}
              data={[
                [
                  'Rebalance Threshold',
                  formatPercentageBN(rebalanceThresholdBN, 4),
                ],
                ['Rebalance Offset', formatPercentageBN(rebalanceOffsetBN, 4)],
                [
                  'Minimum Loan Health',
                  formatAmountBN(minLoanHealthBN, 18, false, 4),
                ],
                [
                  'Loan Health',
                  <Text key="loanHealth">
                    {formatAmountBN(loanHealthBN, 18, false, 4)}
                    {badLoanHealth && (
                      <Badge ml={2} bg="red">
                        Unhealthy
                      </Badge>
                    )}
                  </Text>,
                ],
              ]}
            />
            {badLoanHealth && (
              <StatusButton my={3} onClick={handleSubmit} status={txStatus}>
                Rebalance Loan
              </StatusButton>
            )}
          </>
        )}
      </Loading>
    </Card>
  );
};
